/**
 * API Endpoints organized by module
 *
 * Usage Status Legend:
 * - [ACTIVE] - Endpoint is actively used in the frontend
 * - [PLANNED] - Endpoint exists in backend but not yet integrated in frontend
 * - [DEPRECATED] - Endpoint marked for removal
 *
 * Actively Used Endpoints:
 * - Fleet: FLEET_ASSET_SUMMARIES, FLEET_HEALTH
 * - Comparison: COMPARE_KPIS, COMPARE_KPIS_TRENDS, COMPARE_KPIS_CORRELATIONS
 * - Dashboard: Most DASHBOARD_* endpoints via dashboardApi
 * - Models: MODELS_VERSIONS, MODELS_METRICS, MODELS_FEATURES
 *
 * Planned/Reserved Endpoints (not yet integrated):
 * - Authentication: AUTH_* - Login/logout not implemented in UI
 * - Data Upload: DATA_UPLOAD, DATA_QUALITY
 * - Reports Export: REPORTS_* - Export functionality planned
 * - Admin: ADMIN_* - Admin panel planned
 * - WebSocket: WS_* - Real-time updates planned
 * - Alerts Management: ALERTS_RULES, ALERTS_HISTORY
 */
export const ENDPOINTS = {
  // Health
  HEALTH: '/health',
  HEALTH_LIVE: '/health/live',
  HEALTH_READY: '/health/ready',
  HEALTH_MODELS: '/health/models',
  HEALTH_DRIFT: (assetId: string) => `/health/drift/${assetId}`,

  // Authentication
  AUTH_LOGIN: '/auth/login',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_ME: '/auth/me',
  AUTH_VERIFY: '/auth/verify',
  AUTH_REFRESH: '/auth/refresh',
  AUTH_VALIDATE: '/auth/validate',

  // Predictions
  PREDICT: '/predict',
  PREDICT_ASSET: (assetId: string) => `/predict/${assetId}`,
  PREDICT_SINGLE: (assetId: string) => `/predict/${assetId}/single`,
  PREDICT_UNIFIED: (assetId: string) => `/predict/unified/${assetId}`,
  PREDICT_EXPLANATIONS: '/predict/explanations',
  PREDICT_FEEDBACK: '/predict/feedback',
  PREDICT_FORECAST: (assetId: string) => `/forecast/${assetId}`,
  PREDICT_FORECAST_FOUNDATION: (assetId: string) => `/forecast/foundation/${assetId}`,
  PREDICT_BATCH: '/predict/batch',

  // SHAP Explanations
  EXPLAIN: (assetId: string) => `/explain/${assetId}`,
  EXPLAIN_SUMMARY: (assetId: string) => `/explain/${assetId}/summary`,
  EXPLAIN_SHAP: (assetId: string) => `/explain/${assetId}/shap`,
  EXPLAIN_FEATURE_IMPORTANCE: (assetId: string) => `/explain/${assetId}/feature-importance`,

  // Dashboard
  DASHBOARD: (assetId: string) => `/dashboard/${assetId}`,
  DASHBOARD_COMPARE: '/dashboard/compare',
  DASHBOARD_METRICS: (assetId: string) => `/dashboard/${assetId}/metrics`,
  DASHBOARD_KPIS: (assetId: string) => `/dashboard/${assetId}/kpis`,
  DASHBOARD_OVERVIEW: '/dashboard/overview',
  DASHBOARD_SENSOR_TRENDS: (assetId: string) => `/dashboard/sensor-trends/${assetId}`,
  DASHBOARD_ANOMALY_TIMELINE: (assetId: string) => `/dashboard/anomaly-timeline/${assetId}`,
  DASHBOARD_DATA_QUALITY: (assetId: string) => `/dashboard/data-quality-pie/${assetId}`,
  DASHBOARD_FAULT_COUNTS: '/dashboard/fault-counts-by-asset',
  DASHBOARD_FAULT_SEVERITY: (assetId: string) => `/dashboard/fault-severity/${assetId}`,
  DASHBOARD_RESOLUTION_TIME: (assetId: string) => `/dashboard/resolution-time/${assetId}`,
  DASHBOARD_GAS_COMPOSITION: (assetId: string) => `/dashboard/gas-composition/${assetId}`,
  DASHBOARD_VCP_SETTINGS: (assetId: string) => `/dashboard/vcp-settings/${assetId}`,
  DASHBOARD_CORRELATIONS: (assetId: string) => `/dashboard/correlations/${assetId}`,
  DASHBOARD_KNOWLEDGE_GRAPH: (assetId: string) => `/dashboard/knowledge-graph/${assetId}`,
  DASHBOARD_FAILURE_PREDICTIONS: (assetId: string) => `/dashboard/failure-predictions/${assetId}`,
  DASHBOARD_SME_INSIGHTS: (assetId: string) => `/dashboard/sme-insights/${assetId}`,
  DASHBOARD_AI_INSIGHTS: (assetId: string) => `/dashboard/ai-insights/${assetId}`,
  DASHBOARD_MAINTENANCE_SCHEDULER: (assetId: string) => `/dashboard/maintenance-scheduler/${assetId}`,
  DASHBOARD_ACTION_QUEUE: '/dashboard/action-queue',
  DASHBOARD_ACTION_SCHEDULE: (actionId: string) => `/dashboard/action-queue/${actionId}/schedule`,
  DASHBOARD_ACTION_COMPLETE: (actionId: string) => `/dashboard/action-queue/${actionId}/complete`,
  DASHBOARD_EXECUTIVE_SUMMARY: (assetId: string) => `/dashboard/executive-summary/${assetId}`,
  DASHBOARD_EXECUTIVE_SUMMARY_MULTI: '/dashboard/executive-summary-multi',
  DASHBOARD_STATISTICAL_SUMMARY: (assetId: string) => `/dashboard/statistical-summary/${assetId}`,
  DASHBOARD_DRILL_DOWN: (assetId: string) => `/dashboard/drill-down/${assetId}`,
  DASHBOARD_MODEL_PERFORMANCE: (assetId: string) => `/dashboard/model-performance/${assetId}`,
  DASHBOARD_FLEET_FORECAST: '/dashboard/fleet-forecast',
  DASHBOARD_FLEET_MODEL_METRICS: '/dashboard/fleet-model-metrics',
  DASHBOARD_UPCOMING_EVENTS: '/dashboard/upcoming-events',
  DASHBOARD_WHAT_IF_SCENARIOS: '/dashboard/what-if-scenarios',
  DASHBOARD_HISTORICAL_PATTERNS: (assetId: string) => `/dashboard/historical-patterns/${assetId}`,
  DASHBOARD_MAINTENANCE_EFFICIENCY: '/dashboard/maintenance-efficiency',
  DASHBOARD_EXECUTIVE_TRENDS: '/dashboard/executive-trends',
  DASHBOARD_MODEL_ACCURACY_HISTORY: '/dashboard/model-accuracy-history',
  DASHBOARD_PARTS_PREDICTION: '/dashboard/parts-prediction',
  DASHBOARD_OKR_OBJECTIVES: '/dashboard/okr-objectives',
  DASHBOARD_MAINTENANCE_ACTIONS: '/dashboard/maintenance-actions',
  DASHBOARD_COST_BREAKDOWN: '/dashboard/cost-breakdown',
  DASHBOARD_ASSET_TYPE: (assetId: string) => `/dashboard/asset-type/${assetId}`,

  // Data
  DATA: '/data',
  DATA_ANOMALIES: (assetId: string) => `/data/${assetId}/anomalies`,
  DATA_QUERY: (assetId: string) => `/data/${assetId}/query`,
  DATA_UPLOAD: '/data/upload',
  DATA_AVAILABLE_ASSETS: '/data/assets',
  DATA_QUALITY: '/data/quality',

  // Reports
  REPORTS_GENERATE: (assetId: string) => `/reports/${assetId}/generate`,
  REPORTS_GET: (jobId: string) => `/reports/${jobId}`,
  REPORTS_HTML: (assetId: string) => `/reports/${assetId}/html`,
  REPORTS_MARKDOWN: (assetId: string) => `/reports/${assetId}/markdown`,
  REPORTS_JSON: (assetId: string) => `/reports/${assetId}/json`,
  REPORTS_EXECUTIVE_SUMMARY: (assetId: string) => `/reports/${assetId}/executive_summary`,
  REPORTS_TREND_ANALYSIS: (assetId: string) => `/reports/trend-analysis/${assetId}`,

  // Models
  MODELS: '/models',
  MODELS_STATUS: '/models/status',
  MODELS_SUMMARY: '/models/summary',
  MODELS_ASSETS: '/models/assets',
  MODELS_VALIDATE: '/models/validate',
  MODELS_RELOAD: '/models/reload',
  MODELS_ASSET: (assetId: string) => `/models/${assetId}`,
  MODELS_DEPLOY: (assetId: string) => `/models/${assetId}/deploy`,
  MODELS_VERSIONS: (assetId: string) => `/models/${assetId}/versions`,
  MODELS_METRICS: (assetId: string) => `/models/${assetId}/metrics`,
  MODELS_FEATURES: (assetId: string) => `/models/${assetId}/features`,
  MODELS_EXPORT: (assetId: string) => `/models/${assetId}/export`,
  MODELS_COMPARE: '/models/compare',

  // Alerts
  ALERTS: '/alerts',
  ALERTS_GET: (alertId: string) => `/alerts/${alertId}`,
  ALERTS_SUMMARY: '/alerts/summary',
  ALERTS_STATS: '/alerts/stats',
  ALERTS_RULES: '/alerts/rules',
  ALERTS_RULE: (ruleId: string) => `/alerts/rules/${ruleId}`,
  ALERTS_HISTORY: (assetId: string) => `/alerts/${assetId}/history`,
  ALERTS_ACKNOWLEDGE: (alertId: string) => `/alerts/${alertId}/acknowledge`,
  ALERTS_RESOLVE: (alertId: string) => `/alerts/${alertId}/resolve`,

  // Comparison
  COMPARE_KPIS: '/compare/kpis',
  COMPARE_KPIS_ADVANCED: '/compare/kpis-advanced',
  COMPARE_KPIS_TRENDS: '/compare/kpis-trends',
  COMPARE_KPIS_CORRELATIONS: '/compare/kpis-correlations',
  COMPARE_TARGET_CATEGORIES: '/compare/target-categories',
  COMPARE_VISUALS: (vizType: string) => `/compare/visuals/${vizType}`,
  COMPARE_GAS_COMPOSITION: '/compare/gas-composition',
  COMPARE_VCP_SETTINGS: '/compare/vcp-settings',
  COMPARE_SENSOR_TRENDS: '/compare/sensor-trends',
  COMPARE_ANOMALY_TIMELINES: '/compare/anomaly-timelines',
  COMPARE_EXECUTIVE_SUMMARIES: '/compare/executive-summaries',
  COMPARE_EXPORT_REPORTS: '/compare/export-reports',

  // Fleet (aggregated fleet-level endpoints)
  FLEET_HEALTH: '/fleet/health',
  FLEET_ASSET_SUMMARIES: '/fleet/asset-summaries',
  FLEET_INSIGHTS: '/fleet/insights',
  FLEET_MODEL_PERFORMANCE: '/fleet/model-performance',
  FLEET_METRICS: '/fleet/metrics',
  FLEET_OVERVIEW: '/fleet/overview',

  // WebSocket
  WS_PREDICT: (assetId: string) => `/ws/predict/${assetId}`,
  WS_STREAM: (assetId: string) => `/ws/stream/${assetId}`,
  WS_ALERTS: '/ws/alerts',
  WS_HEALTH: '/ws/health',
  WS_STATUS: '/ws/status',

  // Admin
  ADMIN_STATUS: '/admin/status',
  ADMIN_ASSETS: '/admin/assets',
  ADMIN_STATS: '/admin/stats',
  ADMIN_CONFIG: '/admin/config',
  ADMIN_LOGS: '/admin/logs',
  ADMIN_REGISTRY: '/admin/registry',
  ADMIN_RELOAD_MODELS: '/admin/models/reload',
  ADMIN_CACHE_CLEAR: '/admin/cache',
  ADMIN_SYSTEM_INFO: '/admin/system-info',

  // Metrics
  METRICS: '/metrics',
} as const
